import React, { useState } from 'react';
import { ShoppingCart, Check, Package, Sparkles } from 'lucide-react';
import { Product } from '../types';

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product, quantity: number) => void;
  onProductClick: (product: Product) => void;
}

export const ProductCard: React.FC<ProductCardProps> = React.memo(({
  product,
  onAddToCart,
  onProductClick,
}) => {
  const [isAdded, setIsAdded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddToCart(product, 1);
    setIsAdded(true);
    setTimeout(() => {
      setIsAdded(false);
    }, 1200);
  };

  const handleNavigate = (e: React.MouseEvent) => {
    e.preventDefault();
    onProductClick(product);
  };

  return (
    <div
      id={`product-card-${product.code}`}
      className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-lg hover:border-blue-300 hover:shadow-blue-500/10 transition-all duration-300 flex flex-col justify-between overflow-hidden group h-full"
    >
      {/* 1. Primary Product Image (Clicking redirects to /product/CODE_N1) */}
      <a
        href={`/product/${product.code}_N1`}
        onClick={handleNavigate}
        className="relative aspect-square w-full bg-[#f8fafc] overflow-hidden flex items-center justify-center p-2.5 sm:p-3.5 border-b border-slate-100/90 cursor-pointer group-hover:opacity-95 transition-opacity"
        title={`ดูรายละเอียด ${product.name}`}
      >
        {!imageFailed && product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            width="280"
            height="280"
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="w-full h-full rounded-xl bg-gradient-to-br from-blue-50 via-slate-50 to-white flex flex-col items-center justify-center p-3 text-center border border-blue-100/60">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center text-[#2A59FE] mb-1.5 shadow-2xs">
              <Package className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <span className="text-[11px] font-bold text-[#2A59FE]">
              GIFFARINE
            </span>
            <span className="text-[10px] text-slate-500 font-medium mt-0.5">
              รหัส {product.code}
            </span>
          </div>
        )}
      </a>

      {/* Card Content (Strictly Code, Name, Price, Add to Cart Button) */}
      <div className="p-3 sm:p-3.5 flex-1 flex flex-col justify-between">
        <div>
          {/* 2. Product Code */}
          <div className="flex items-center justify-between gap-1 mb-1">
            <span className="text-[10.5px] sm:text-[11.5px] font-bold text-slate-400 font-mono">
              รหัส {product.code}
            </span>
            {product.pv > 0 && (
              <span className="text-[9.5px] sm:text-[10.5px] font-extrabold text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200/60">
                {product.pv} PV
              </span>
            )}
          </div>

          {/* 3. Product Name */}
          <a
            href={`/product/${product.code}_N1`}
            onClick={handleNavigate}
            className="text-xs sm:text-[13.5px] font-bold text-slate-800 line-clamp-2 min-h-[32px] sm:min-h-[38px] leading-snug group-hover:text-[#2A59FE] transition-colors cursor-pointer block"
            title={product.name}
          >
            {product.name}
          </a>
        </div>

        <div className="mt-3 pt-2.5 border-t border-slate-100 flex flex-col gap-2.5">
          {/* 4. Price & Member Price */}
          <div className="flex items-baseline justify-between gap-1 flex-wrap">
            <span className="text-sm sm:text-base font-black text-slate-900 tracking-tight">
              ฿{product.price.toLocaleString()}
            </span>
            {product.memberPrice && product.memberPrice !== product.price ? (
              <span className="text-[10.5px] sm:text-xs text-[#E11D48] font-bold">
                สมาชิก ฿{product.memberPrice.toLocaleString()}
              </span>
            ) : null}
          </div>

          {/* 5. Add to Cart Button (Royal Blue Gradient #2A59FE -> #3B66FE -> #4F46E5) */}
          <button
            type="button"
            id={`btn-add-cart-${product.code}`}
            onClick={handleAdd}
            disabled={isAdded}
            className={`w-full py-2 sm:py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 transition-all active:scale-[0.97] cursor-pointer shadow-xs ${
              isAdded
                ? 'bg-emerald-600 text-white'
                : 'bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 text-white shadow-blue-500/15'
            }`}
          >
            {isAdded ? (
              <>
                <Check className="w-3.5 h-3.5" />
                <span>เพิ่มแล้ว</span>
              </>
            ) : (
              <>
                <ShoppingCart className="w-3.5 h-3.5" />
                <span>ใส่ตะกร้า</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

ProductCard.displayName = 'ProductCard';
